import { motion } from "framer-motion";
import { PhoneCall, Map, Plane, FileText } from "lucide-react";

const STEPS = [
  { icon: PhoneCall, title: "Step 1: Free Field Assessment & Quote", body: "Call us or submit the form. We assess your acreage, crop, target pest, weather window and chemical program, then come back with a free, transparent quote within 24 hours." },
  { icon: Map, title: "Step 2: Custom Spray Plan Creation", body: "Our team geofences your field, marks obstacles, sets buffer zones around beehives and homes, and builds a GPS-loaded prescription flight plan tailored to your crop." },
  { icon: Plane, title: "Step 3: FAA-Compliant Drone Application", body: "FAA Part 137 certified pilots spray your field with GPS-guided swath control. Every flight is logged, geofenced and applied within EPA and FDACS label requirements." },
  { icon: FileText, title: "Step 4: Post-Spray Coverage Report", body: "You receive a flight log, applied-rate map, weather snapshot and timestamped proof of application — valid for organic certification audits and crop-insurance claims." },
];

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-accent mb-4">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary font-bold leading-tight">
              From call to <span className="text-accent">crop-saved</span> in days.
            </h2>
          </div>
          <p className="text-base text-muted-foreground max-w-md">
            We handle the FAA paperwork, the chemical sourcing and the spray. You just walk the field afterwards.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  data-testid={`process-step-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="relative"
                >
                  <div className="relative z-10 h-24 w-24 mx-auto rounded-3xl bg-card border border-border shadow-card flex items-center justify-center">
                    <Icon className="h-8 w-8 text-primary" />
                    <div className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-accent text-accent-foreground text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="mt-6 font-display text-lg font-bold text-primary text-center">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground text-center max-w-xs mx-auto">{s.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
